import React, { useMemo } from 'react';
import { criticalityBandKey, isCriticalityNotSet } from '../../../utils/criticalityBands';
import { PackageOpen } from 'lucide-react';
import MaterialCard from './MaterialCard';

const mapStatusToType = (status) => {
    if (!status) return 'default';
    const s = (status || '').toLowerCase();
    if (s === 'ok') return 'success';
    if (s === 'short' || s === 'critical') return 'error';
    if (s === 'excess') return 'warning';
    return 'default';
};

// Ranges match MaterialCard: >=150 Excess | >=125 OK | >=100 Warning | <100 Critical
const FILTER_TO_BAND = { excess: 'green', ok: 'yellow', warning: 'orange', critical: 'red', not_set: 'not_set' };
const matchesStatusFilter = (stockCriticality, statusFilter) => {
    if (!statusFilter) return true;
    const band = FILTER_TO_BAND[statusFilter];
    // "Not set" (NULL) matches only its own filter, never Critical.
    return band ? criticalityBandKey(stockCriticality) === band : true;
};

const MaterialSummary = ({ monthlyInventory, categoryFilter = '', statusFilter = '' }) => {
    const items = useMemo(() => {
        const apiItems = monthlyInventory?.items;
        if (!apiItems?.length) return [];
        const mapped = apiItems.map((item) => ({
            id: item.itemId,
            name: item.itemName,
            unit: item.unit,
            opening: item.opening ?? 0,
            purchased: item.in_transit ?? 0,
            consumed: item.consumed ?? 0,
            closing: item.closing ?? 0,
            totalSpend: item.totalSpend ?? 0,
            status: item.status,
            percentage: item.statusPercent ?? null,
            statusType: mapStatusToType(item.status),
            stock_criticality: isCriticalityNotSet(item.stock_criticality) ? null : Number(item.stock_criticality),
            parent_category_id: item.parent_category_id,
        }));
        return mapped.filter((item) => {
            const matchCategory = !categoryFilter || String(item.parent_category_id) === String(categoryFilter);
            const matchStatus = matchesStatusFilter(item.stock_criticality, statusFilter);
            return matchCategory && matchStatus;
        });
    }, [monthlyInventory, categoryFilter, statusFilter]);

    const hasItems = items.length > 0;

    return (
        <div>
            <h2 className="text-lg font-bold text-gray-900 mb-6">Material Summary</h2>
            {hasItems ? (
                <div className="flex flex-col gap-1">
                    {items.map((item) => (
                        <MaterialCard key={item.id} data={item} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-16 px-6 bg-white rounded-2xl border border-gray-100 border-dashed shadow-sm">
                    <PackageOpen className="w-16 h-16 text-gray-300 mb-4" strokeWidth={1.25} />
                    <p className="text-lg font-semibold text-gray-600 mb-1">No inventory data</p>
                    <p className="text-sm text-gray-400 text-center max-w-sm">
                        There is no material summary for the selected month. Try another month or project.
                    </p>
                </div>
            )}
        </div>
    );
};

export default MaterialSummary;
